import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getPaginationItems } from '../../utils/paginationItems'

type TeamListPaginationProps = {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  testId?: string
}

const StyledFooter = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid ${(props) => props.theme.color.neutral.color8};
  background-color: ${(props) => props.theme.palette.white};
`

const StyledPageButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background-color: ${(props) => props.theme.palette.white};
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.theme.color.status.primary
        : props.theme.color.neutral.color6};
  color: ${(props) =>
    props.$active
      ? props.theme.color.status.primary
      : props.theme.color.neutral.color3};
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const StyledEllipsis = styled.span`
  min-width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  color: ${(props) => props.theme.color.neutral.color5};
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  user-select: none;
`

const TeamListPagination: React.FC<TeamListPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)

  if (totalPages <= 1) {
    return null
  }

  const goTo = (page: number) => onPageChange(page)
  // Windowed list (first/last + neighbours + ellipses) so a large page count
  // doesn't render hundreds of buttons that overflow the list card.
  const items = getPaginationItems(currentPage, totalPages)

  return (
    <StyledFooter
      aria-label={t(reportsConstants.TEAM_LIST_PAGINATION_LABEL)}
      data-testid={testId}
    >
      <StyledPageButton
        type="button"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={t(reportsConstants.TEAM_LIST_PAGINATION_PREV)}
        data-testid={testId ? `${testId}-prev` : undefined}
      >
        <LeftOutlined />
      </StyledPageButton>
      {items.map((item) =>
        typeof item === 'number' ? (
          <StyledPageButton
            key={item}
            type="button"
            $active={item === currentPage}
            onClick={() => goTo(item)}
            aria-label={t(reportsConstants.TEAM_LIST_PAGINATION_PAGE, {
              page: item
            })}
            aria-current={item === currentPage ? 'page' : undefined}
            data-testid={testId ? `${testId}-page-${item}` : undefined}
          >
            {item}
          </StyledPageButton>
        ) : (
          <StyledEllipsis key={item} aria-hidden="true">
            …
          </StyledEllipsis>
        )
      )}
      <StyledPageButton
        type="button"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={t(reportsConstants.TEAM_LIST_PAGINATION_NEXT)}
        data-testid={testId ? `${testId}-next` : undefined}
      >
        <RightOutlined />
      </StyledPageButton>
    </StyledFooter>
  )
}

export default TeamListPagination
